/**
 * Bill Calendar — month view of bill due dates
 */

import React, { useMemo, useState } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
} from 'date-fns';
import { Check, Pencil, Trash2, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import clsx from 'clsx';
import { useBills, useDeleteBill, useMarkBillPaid } from '../../hooks/useBudgetOverview';
import { useRegion } from '../../contexts/RegionContext';
import type { Bill } from '../../types/bill.types';
import { getBillDisplayName } from '../../types/bill.types';
import { PrimaryCategoryChip } from '../../components/categories/PrimaryCategoryChip';
import {
  buildBillCalendarOccurrences,
  groupOccurrencesByDate,
  statusDotClass,
  type BillCalendarOccurrence,
} from '../../utils/bill-calendar';
import { getBillRelativeTiming } from '../../utils/budget-calculations';

interface BillCalendarProps {
  onEdit: (bill: Bill) => void;
}

function formatIso(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

function getStatusBadge(statusLabel: string): string {
  switch (statusLabel.toLowerCase()) {
    case 'due':
      return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300';
    case 'overdue':
      return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
    case 'paid':
      return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
    default:
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
  }
}

export function BillCalendar({ onEdit }: BillCalendarProps): React.ReactElement {
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [selectedDate, setSelectedDate] = useState<string>(() => formatIso(new Date()));
  const { data: bills = [], isLoading } = useBills();
  const deleteBill = useDeleteBill();
  const markPaid = useMarkBillPaid();
  const { formatCurrency } = useRegion();

  const rangeStart = formatIso(startOfMonth(month));
  const rangeEnd = formatIso(endOfMonth(month));

  const byDate = useMemo(() => {
    const occurrences = buildBillCalendarOccurrences(bills, rangeStart, rangeEnd);
    return groupOccurrencesByDate(occurrences);
  }, [bills, rangeStart, rangeEnd]);

  const calendarStart = startOfWeek(startOfMonth(month), { weekStartsOn: 0 });
  const calendarEnd = endOfWeek(endOfMonth(month), { weekStartsOn: 0 });
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const today = new Date();

  const selectedOccurrences: BillCalendarOccurrence[] = byDate.get(selectedDate) ?? [];

  const handleDelete = async (bill: Bill) => {
    if (!window.confirm(`Delete "${getBillDisplayName(bill)}"? This cannot be undone.`)) return;
    try {
      await deleteBill.mutateAsync(bill.id);
    } catch (e) {
      console.error('Failed to delete bill:', e);
    }
  };

  const handleMarkPaid = async (bill: Bill) => {
    try {
      await markPaid.mutateAsync(bill.id);
    } catch (e) {
      console.error('Failed to mark bill paid:', e);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" aria-label="Loading bills" />
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Bills</h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setMonth((m) => subMonths(m, 1))}
              className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label="Previous month"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 min-w-[8rem] text-center">
              {format(month, 'MMMM yyyy')}
            </span>
            <button
              type="button"
              onClick={() => setMonth((m) => addMonths(m, 1))}
              className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label="Next month"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          {bills.length} {bills.length === 1 ? 'bill' : 'bills'} tracked
        </p>
      </div>

      <div className="p-4 sm:p-6">
        <div className="grid grid-cols-7 gap-1 mb-1">
          {weekDays.map((day, index) => (
            <div
              key={index}
              className="text-xs font-medium text-gray-500 dark:text-gray-400 text-center py-1"
            >
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {days.map((day) => {
            const iso = formatIso(day);
            const dayBills = byDate.get(iso) ?? [];
            const inMonth = isSameMonth(day, month);
            const isToday = isSameDay(day, today);
            const isSelected = iso === selectedDate;
            const showAmount = dayBills.length > 0 && dayBills.length <= 3;
            const total = dayBills.reduce((sum, o) => sum + o.bill.amount, 0);

            return (
              <button
                key={iso}
                type="button"
                onClick={() => setSelectedDate(iso)}
                className={clsx(
                  'min-h-[4.5rem] rounded-lg p-1.5 text-left transition-colors border',
                  isToday
                    ? 'bg-green-50 dark:bg-green-950/50 border-transparent hover:bg-green-100 dark:hover:bg-green-900/50'
                    : dayBills.length > 0
                      ? inMonth
                        ? 'bg-blue-50 dark:bg-blue-950/50 border-transparent hover:bg-blue-100 dark:hover:bg-blue-900/50'
                        : 'bg-blue-50/70 dark:bg-blue-950/30 border-transparent text-gray-500 dark:text-gray-400'
                      : inMonth
                        ? 'bg-white dark:bg-gray-800 border-transparent hover:bg-gray-50 dark:hover:bg-gray-700/40'
                        : 'bg-gray-50/50 dark:bg-gray-900/30 border-transparent text-gray-400',
                  isSelected && 'border-blue-500 ring-1 ring-blue-500',
                  isToday && !isSelected && 'border-green-300 dark:border-green-700'
                )}
                aria-label={`${format(day, 'd MMMM yyyy')}${
                  dayBills.length ? `, ${dayBills.length} bills` : ''
                }`}
                aria-pressed={isSelected}
              >
                <div
                  className={clsx(
                    'text-xs font-medium mb-1',
                    isToday
                      ? 'text-green-900 dark:text-green-100'
                      : dayBills.length > 0 && inMonth
                        ? 'text-gray-900 dark:text-gray-100'
                        : !inMonth
                          ? 'text-gray-400'
                          : 'text-gray-900 dark:text-gray-100'
                  )}
                >
                  {format(day, 'd')}
                </div>
                {dayBills.length > 0 && (
                  <div className="space-y-1">
                    <div className="flex flex-wrap gap-0.5">
                      {dayBills.slice(0, 3).map((o) => (
                        <span
                          key={`${o.bill.id}-${o.occurrenceDate}`}
                          className={clsx('h-1.5 w-1.5 rounded-full', statusDotClass(o.status))}
                        />
                      ))}
                      {dayBills.length > 3 && (
                        <span
                          className={clsx(
                            'text-[10px] font-medium leading-none',
                            isToday
                              ? 'text-green-900 dark:text-green-100'
                              : 'text-gray-700 dark:text-gray-200'
                          )}
                        >
                          +{dayBills.length - 3}
                        </span>
                      )}
                    </div>
                    {showAmount && (
                      <p
                        className={clsx(
                          'text-[10px] font-medium truncate',
                          isToday
                            ? 'text-green-900 dark:text-green-100'
                            : 'text-gray-800 dark:text-gray-100'
                        )}
                      >
                        {formatCurrency(total)}
                      </p>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 p-4 sm:p-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
          {format(new Date(`${selectedDate}T00:00:00`), 'EEEE, d MMMM yyyy')}
        </h3>
        {selectedOccurrences.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No bills due on this day.</p>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {selectedOccurrences.map(({ bill, status, occurrenceDate }) => {
              const label = getBillDisplayName(bill);
              const timing = getBillRelativeTiming(occurrenceDate, status, new Date(), bill.autopay);
              return (
                <div
                  key={`${bill.id}-${selectedDate}`}
                  className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">{label}</h4>
                      {bill.primaryCategoryName && (
                        <PrimaryCategoryChip
                          name={bill.primaryCategoryName}
                          color={bill.primaryCategoryColor}
                        />
                      )}
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${getStatusBadge(status)}`}
                      >
                        {timing}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                      {bill.frequency.replace('_', ' ')}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {formatCurrency(bill.amount)}
                    </span>
                    <div className="flex items-center gap-1">
                      {status !== 'paid' && !bill.autopay && (
                        <button
                          type="button"
                          onClick={() => handleMarkPaid(bill)}
                          disabled={markPaid.isPending}
                          className="p-2 text-green-600 hover:text-green-700 dark:text-green-400 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 disabled:opacity-50"
                          aria-label={`Mark ${label} as paid`}
                          title="Mark paid"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => onEdit(bill)}
                        className="p-2 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                        aria-label={`Edit ${label}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(bill)}
                        disabled={deleteBill.isPending}
                        className="p-2 text-gray-500 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                        aria-label={`Delete ${label}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
